/**
 * URL helpers for backend API routing.
 *
 * The app historically used REACT_APP_API_URL as an *admin API base* (including /api/admin).
 * Some features (auth/images) need access to the public /api routes or the backend origin.
 *
 * This module keeps backward compatibility and provides normalized URLs.
 */

const DEFAULT_BACKEND_ORIGIN = 'https://localhost:7064';

function trimTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

/**
 * Resolves the Admin API base URL.
 *
 * Accepted inputs:
 * - https://host/api/admin (preferred)
 * - https://host/api (will append /admin)
 * - https://host (will append /api/admin)
 */
export function resolveAdminApiUrl(envUrl?: string): string {
  const raw = envUrl?.trim();
  if (!raw) {
    return `${DEFAULT_BACKEND_ORIGIN}/api/admin`;
  }

  if (raw.includes('/api/admin')) {
    return raw;
  }

  const normalized = trimTrailingSlash(raw);

  if (normalized.endsWith('/api')) {
    return `${normalized}/admin`;
  }

  return `${normalized}/api/admin`;
}

/**
 * Resolves the public Auth API base URL ("/api").
 *
 * Accepted inputs:
 * - https://host/api/admin (will replace with /api)
 * - https://host/api
 * - https://host (will append /api)
 */
export function resolveAuthApiUrl(envUrl?: string): string {
  const raw = envUrl?.trim();
  if (!raw) {
    return `${DEFAULT_BACKEND_ORIGIN}/api`;
  }

  if (raw.includes('/api/admin')) {
    return raw.replace('/api/admin', '/api');
  }

  const normalized = trimTrailingSlash(raw);

  if (normalized.endsWith('/api')) {
    return normalized;
  }

  return `${normalized}/api`;
}

/**
 * Resolves backend origin (scheme + host + optional port), without /api or /api/admin.
 */
export function resolveBackendOrigin(envUrl?: string): string {
  const raw = envUrl?.trim();
  if (!raw) {
    return DEFAULT_BACKEND_ORIGIN;
  }

  if (raw.includes('/api/admin')) {
    return trimTrailingSlash(raw.replace('/api/admin', ''));
  }

  if (raw.includes('/api')) {
    return trimTrailingSlash(raw.replace('/api', ''));
  }

  return trimTrailingSlash(raw);
}
